/**
 * TRANSACTIONS PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Features:
 * - Dual currency support (USD & PKR)
 * - Filter by year, month, currency, status
 * - Add/edit transactions with client selection
 * - Summary statistics with StatCard components
 * - Liquid Glass transaction list with animations
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DollarSign,
  Search,
  Calendar,
  TrendingUp,
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  Wallet,
  ArrowUpRight,
  CreditCard,
  ChevronRight
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input, SearchInput } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { SkeletonTable, SkeletonStatCard } from '../../components/ui/Skeleton'
import { apiClient } from '../../lib/api'

// Animation configuration
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig
  }
}

interface Transaction {
  id: number
  date: string
  client_id: number
  client_name: string | null
  client_company: string | null
  amount: number
  currency: 'USD' | 'PKR'
  payment_method: string | null
  description: string | null
  status: 'Pending' | 'Completed' | 'Failed'
  created_at: string
}

interface Client {
  id: number
  name: string
  company: string | null
  payment_method: string
}

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
]

export function TransactionsPage() {
  const [loading, setLoading] = useState(true)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString())
  const [monthFilter, setMonthFilter] = useState('all')
  const [currencyFilter, setCurrencyFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    client_id: 0,
    amount: 0,
    currency: 'USD' as 'USD' | 'PKR',
    payment_method: 'Wise',
    description: '',
    status: 'Completed' as 'Pending' | 'Completed' | 'Failed'
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchData()
  }, [yearFilter, monthFilter, currencyFilter, statusFilter])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams()
      if (yearFilter) params.append('year', yearFilter)
      if (monthFilter !== 'all') params.append('month', monthFilter)
      if (currencyFilter !== 'all') params.append('currency', currencyFilter)
      if (statusFilter !== 'all') params.append('status', statusFilter)

      const [transResponse, clientsResponse] = await Promise.all([
        apiClient.get(`/transactions?${params.toString()}`),
        apiClient.get('/clients')
      ])

      if (transResponse.success) {
        setTransactions(transResponse.data || [])
      }

      if (clientsResponse.success) {
        setClients(clientsResponse.data || [])
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
      setError('Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!formData.date) errors.date = 'Date is required'
    if (!formData.client_id) errors.client_id = 'Client is required'
    if (!formData.amount || formData.amount <= 0) errors.amount = 'Amount must be greater than 0'

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAdd = async () => {
    if (!validateForm()) return

    try {
      setSaving(true)
      setError(null)
      const response = await apiClient.post('/transactions', formData)
      if (response.success) {
        await fetchData()
        setIsAddModalOpen(false)
        resetForm()
      }
    } catch (err: any) {
      console.error('Failed to add transaction:', err)
      setError(err.message || 'Failed to add transaction')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async () => {
    if (!validateForm() || !selectedTransaction) return

    try {
      setSaving(true)
      setError(null)
      const response = await apiClient.put(`/transactions/${selectedTransaction.id}`, formData)
      if (response.success) {
        await fetchData()
        setIsEditModalOpen(false)
        resetForm()
      }
    } catch (err: any) {
      console.error('Failed to update transaction:', err)
      setError(err.message || 'Failed to update transaction')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedTransaction) return

    try {
      setDeleting(true)
      const response = await apiClient.delete(`/transactions/${selectedTransaction.id}`)
      if (response.success) {
        await fetchData()
        setIsDeleteDialogOpen(false)
        setSelectedTransaction(null)
      }
    } catch (err: any) {
      console.error('Failed to delete transaction:', err)
      setError(err.message || 'Failed to delete transaction')
    } finally {
      setDeleting(false)
    }
  }

  const openEditModal = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
    setFormData({
      date: transaction.date,
      client_id: transaction.client_id,
      amount: transaction.amount,
      currency: transaction.currency,
      payment_method: transaction.payment_method || 'Wise',
      description: transaction.description || '',
      status: transaction.status
    })
    setIsEditModalOpen(true)
  }

  const openDeleteDialog = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
    setIsDeleteDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      client_id: 0,
      amount: 0,
      currency: 'USD',
      payment_method: 'Wise',
      description: '',
      status: 'Completed'
    })
    setFormErrors({})
    setSelectedTransaction(null)
  }

  const formatCurrency = (amount: number, currency: 'USD' | 'PKR') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(amount)
    }
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Filter transactions by search
  const filteredTransactions = transactions.filter(t => {
    const searchLower = searchQuery.toLowerCase()
    return (
      (t.client_name?.toLowerCase() || '').includes(searchLower) ||
      (t.client_company?.toLowerCase() || '').includes(searchLower) ||
      (t.description?.toLowerCase() || '').includes(searchLower)
    )
  })

  // Calculate summary stats
  const usdTotal = transactions
    .filter(t => t.currency === 'USD' && t.status === 'Completed')
    .reduce((sum, t) => sum + Number(t.amount), 0)
  const pkrTotal = transactions
    .filter(t => t.currency === 'PKR' && t.status === 'Completed')
    .reduce((sum, t) => sum + Number(t.amount), 0)
  const pendingCount = transactions.filter(t => t.status === 'Pending').length
  const totalCount = transactions.length

  // Loading state
  if (loading) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
            <div className="h-4 w-64 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-24 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
            <div className="h-10 w-36 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
          </div>
        </div>
        <StatCardGrid columns={4}>
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </StatCardGrid>
        <SkeletonTable rows={8} />
      </div>
    )
  }

  return (
    <motion.div
      className="p-6 max-w-[1600px] mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
            Transactions
          </h1>
          <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mt-1">
            Track all income transactions from clients
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Transaction
          </Button>
        </div>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className="mb-6"
          >
            <GlassCard className="border border-red/20 bg-red/5">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-xl bg-red/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-red" />
                </div>
                <div className="flex-1">
                  <p className="text-red font-medium">{error}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Stats */}
      <motion.div variants={itemVariants}>
        <StatCardGrid columns={4}>
          <StatCard
            title="Total USD Income"
            value={formatCurrency(usdTotal, 'USD')}
            icon={DollarSign}
            color="blue"
            badge={<Badge variant="blue">USD</Badge>}
          />
          <StatCard
            title="Total PKR Income"
            value={formatCurrency(pkrTotal, 'PKR')}
            icon={Wallet}
            color="green"
            badge={<Badge variant="green">PKR</Badge>}
          />
          <StatCard
            title="Pending Transactions"
            value={pendingCount.toString()}
            icon={Calendar}
            color="orange"
            badge={<Badge variant="orange">Pending</Badge>}
          />
          <StatCard
            title="Total Transactions"
            value={totalCount.toString()}
            icon={CreditCard}
            color="purple"
            badge={<Badge variant="purple">All Time</Badge>}
          />
        </StatCardGrid>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants} className="mt-6">
        <GlassCard>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2">
                <SearchInput
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select
                value={yearFilter}
                onChange={setYearFilter}
                options={[
                  { value: '2024', label: '2024' },
                  { value: '2025', label: '2025' },
                  { value: '2026', label: '2026' }
                ]}
              />
              <Select
                value={monthFilter}
                onChange={setMonthFilter}
                options={[
                  { value: 'all', label: 'All Months' },
                  ...MONTHS
                ]}
              />
              <Select
                value={currencyFilter}
                onChange={setCurrencyFilter}
                options={[
                  { value: 'all', label: 'All Currencies' },
                  { value: 'USD', label: 'USD Only' },
                  { value: 'PKR', label: 'PKR Only' }
                ]}
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'Completed', label: 'Completed' },
                  { value: 'Pending', label: 'Pending' },
                  { value: 'Failed', label: 'Failed' }
                ]}
              />
            </div>
          </div>
        </GlassCard>
      </motion.div>

      {/* Transactions List */}
      <motion.div variants={itemVariants} className="mt-6">
        <ContentCard>
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-16">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={springConfig}
                className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue/10 to-purple/10 flex items-center justify-center"
              >
                <DollarSign className="w-10 h-10 text-light-text-quaternary dark:text-dark-text-quaternary" />
              </motion.div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
                No transactions found
              </h3>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mb-6">
                {searchQuery ? 'Try adjusting your search or filters' : 'Start by adding your first transaction'}
              </p>
              <Button
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setIsAddModalOpen(true)}
              >
                Add Transaction
              </Button>
            </div>
          ) : (
            <motion.div
              className="divide-y divide-black/[0.04] dark:divide-white/[0.04]"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {filteredTransactions.map((transaction, index) => (
                <motion.div
                  key={transaction.id}
                  variants={listItemVariants}
                  custom={index}
                  whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                  className="flex items-center gap-4 p-4 transition-colors group"
                >
                  {/* Currency Icon */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                      transaction.currency === 'USD'
                        ? 'bg-gradient-to-br from-blue/10 to-blue/5'
                        : 'bg-gradient-to-br from-green/10 to-green/5'
                    }`}
                  >
                    {transaction.currency === 'USD' ? (
                      <DollarSign className="w-6 h-6 text-blue" />
                    ) : (
                      <Wallet className="w-6 h-6 text-green" />
                    )}
                  </motion.div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary truncate">
                          {transaction.client_name || 'Unknown Client'}
                        </h4>
                        {transaction.client_company && (
                          <p className="text-footnote text-light-text-tertiary dark:text-dark-text-tertiary truncate">
                            {transaction.client_company}
                          </p>
                        )}
                        <p className="text-footnote text-light-text-quaternary dark:text-dark-text-quaternary mt-1 truncate">
                          {transaction.description || 'No description'}
                        </p>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-1">
                          <ArrowUpRight className={`w-4 h-4 ${
                            transaction.currency === 'USD' ? 'text-blue' : 'text-green'
                          }`} />
                          <span className={`text-title3 font-bold ${
                            transaction.currency === 'USD' ? 'text-blue' : 'text-green'
                          }`}>
                            {formatCurrency(transaction.amount, transaction.currency)}
                          </span>
                        </div>
                        <div className="mt-1">
                          <StatusBadge
                            status={
                              transaction.status === 'Completed' ? 'active' :
                              transaction.status === 'Pending' ? 'pending' : 'inactive'
                            }
                          >
                            {transaction.status}
                          </StatusBadge>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mt-2">
                      <span className="flex items-center gap-1.5 text-caption1 text-light-text-quaternary dark:text-dark-text-quaternary">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(transaction.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      {transaction.payment_method && (
                        <Badge variant="purple" size="sm">
                          {transaction.payment_method}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => openEditModal(transaction)}
                      className="p-2.5 rounded-xl hover:bg-blue/10 text-blue transition-colors"
                      title="Edit"
                    >
                      <Edit3 className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => openDeleteDialog(transaction)}
                      className="p-2.5 rounded-xl hover:bg-red/10 text-red transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>

                  {/* Chevron indicator */}
                  <ChevronRight className="w-5 h-5 text-light-text-quaternary dark:text-dark-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity" />
                </motion.div>
              ))}
            </motion.div>
          )}
        </ContentCard>
      </motion.div>

      {/* Results count */}
      {filteredTransactions.length > 0 && (
        <motion.div
          variants={itemVariants}
          className="mt-4 text-center"
        >
          <p className="text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
            Showing {filteredTransactions.length} of {totalCount} transactions
          </p>
        </motion.div>
      )}

      {/* Add Transaction Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false)
          resetForm()
        }}
        title="Add Transaction"
        size="md"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleAdd(); }} className="space-y-5">
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            error={formErrors.date}
          />

          <div>
            <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
              Client
            </label>
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: parseInt(e.target.value) })}
              className="w-full h-12 px-4 rounded-2xl liquid-material-regular liquid-lens text-body text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue/30 transition-all"
              required
            >
              <option value={0}>Select a client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name} {client.company ? `(${client.company})` : ''}
                </option>
              ))}
            </select>
            {formErrors.client_id && (
              <p className="text-red text-caption1 mt-1.5">{formErrors.client_id}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
              required
              error={formErrors.amount}
            />
            <Select
              label="Currency"
              value={formData.currency}
              onChange={(value) => setFormData({ ...formData, currency: value as 'USD' | 'PKR' })}
              options={[
                { value: 'USD', label: 'USD' },
                { value: 'PKR', label: 'PKR' }
              ]}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Payment Method"
              value={formData.payment_method}
              onChange={(value) => setFormData({ ...formData, payment_method: value })}
              options={[
                { value: 'Wise', label: 'Wise' },
                { value: 'Remitly', label: 'Remitly' },
                { value: 'Local Bank', label: 'Local Bank' },
                { value: 'Other', label: 'Other' }
              ]}
            />
            <Select
              label="Status"
              value={formData.status}
              onChange={(value) => setFormData({ ...formData, status: value as 'Pending' | 'Completed' | 'Failed' })}
              options={[
                { value: 'Completed', label: 'Completed' },
                { value: 'Pending', label: 'Pending' },
                { value: 'Failed', label: 'Failed' }
              ]}
            />
          </div>

          <div>
            <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 rounded-2xl liquid-material-regular liquid-lens text-body text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue/30 transition-all resize-none"
              rows={3}
              placeholder="Monthly subscription, setup fee, etc."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => {
                setIsAddModalOpen(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={saving}
              isLoading={saving}
            >
              Add Transaction
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Transaction Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          resetForm()
        }}
        title="Edit Transaction"
        size="md"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleEdit(); }} className="space-y-5">
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            error={formErrors.date}
          />

          <div>
            <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
              Client
            </label>
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: parseInt(e.target.value) })}
              className="w-full h-12 px-4 rounded-2xl liquid-material-regular liquid-lens text-body text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue/30 transition-all"
              required
            >
              <option value={0}>Select a client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name} {client.company ? `(${client.company})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
              required
              error={formErrors.amount}
            />
            <Select
              label="Currency"
              value={formData.currency}
              onChange={(value) => setFormData({ ...formData, currency: value as 'USD' | 'PKR' })}
              options={[
                { value: 'USD', label: 'USD' },
                { value: 'PKR', label: 'PKR' }
              ]}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Payment Method"
              value={formData.payment_method}
              onChange={(value) => setFormData({ ...formData, payment_method: value })}
              options={[
                { value: 'Wise', label: 'Wise' },
                { value: 'Remitly', label: 'Remitly' },
                { value: 'Local Bank', label: 'Local Bank' },
                { value: 'Other', label: 'Other' }
              ]}
            />
            <Select
              label="Status"
              value={formData.status}
              onChange={(value) => setFormData({ ...formData, status: value as 'Pending' | 'Completed' | 'Failed' })}
              options={[
                { value: 'Completed', label: 'Completed' },
                { value: 'Pending', label: 'Pending' },
                { value: 'Failed', label: 'Failed' }
              ]}
            />
          </div>

          <div>
            <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 rounded-2xl liquid-material-regular liquid-lens text-body text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue/30 transition-all resize-none"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => {
                setIsEditModalOpen(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={saving}
              isLoading={saving}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setSelectedTransaction(null)
        }}
        onConfirm={handleDelete}
        title="Delete Transaction"
        message={`Are you sure you want to delete this transaction of ${
          selectedTransaction ? formatCurrency(selectedTransaction.amount, selectedTransaction.currency) : ''
        }? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />
    </motion.div>
  )
}
